import { TextAreaField } from '@aws-amplify/ui-react'
import { UploadFileOutlined } from '@mui/icons-material'
import { Paper, Stack, Typography } from '@mui/material'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ConfirmationButton from '@/components/inputs/ConfirmationButton'
import MainContentContainer from '@/components/layouts/MainContentContainer'
import { useAlertContext } from '@/contexts/AlertContext'
import { useMultiSiteBulkImportRestrictionsMutation } from '@/features/site/hooks/useMultiSiteBulkImportRestrictionsMutation'
import {
  parseCommaSeparatedIds,
  validateISODateFormat,
  validateTimeFormat,
  validateUUID,
} from '@/features/site/utils/tsvValidation'
import { errorHandler } from '@/utils/errorHandler'

type ParsedMultiSiteRestriction = {
  siteId: string
  resourceIds: string[]
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  location: string
}

// Use common translation keys shared by both bulk import pages
const COMMON_BULK_IMPORT_PREFIX = 'common.bulk_import'
const MULTI_SITE_PREFIX = 'multi_site_bulk_import'

const parseTsvContent = (content: string, t: TFunction<'translation'>): ParsedMultiSiteRestriction[] => {
  const lines = content.trim().split('\n')

  // Skip header row
  const dataLines = lines.slice(1).filter((line) => line.trim())

  if (dataLines.length === 0) {
    throw new Error(t(`${COMMON_BULK_IMPORT_PREFIX}.error.no_data_rows`))
  }

  return dataLines.map((line, index) => {
    const rowNumber = index + 2 // Account for header and 0-based index
    const columns = line.split('\t')

    if (columns.length !== 7) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_columns`, {
          row: rowNumber,
          expected: 7,
          got: columns.length,
        }),
      )
    }

    const [siteId, resourceIdsStr, startDate, startTime, endDate, endTime, location] = columns

    if (!validateUUID(siteId.trim())) {
      throw new Error(
        t(`${MULTI_SITE_PREFIX}.error.invalid_site_uuid`, {
          row: rowNumber,
          uuid: siteId,
        }),
      )
    }

    // Parse and validate resource IDs
    const resourceIds = parseCommaSeparatedIds(resourceIdsStr)

    if (resourceIds.length === 0) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.no_assets`, {
          row: rowNumber,
        }),
      )
    }

    // Validate each UUID
    resourceIds.forEach((id) => {
      if (!validateUUID(id)) {
        throw new Error(
          t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_uuid`, {
            row: rowNumber,
            uuid: id,
          }),
        )
      }
    })

    // Validate dates
    if (!validateISODateFormat(startDate) || !validateISODateFormat(endDate)) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_date`, {
          row: rowNumber,
        }),
      )
    }

    // Validate times
    if (!validateTimeFormat(startTime) || !validateTimeFormat(endTime)) {
      throw new Error(
        t(`${COMMON_BULK_IMPORT_PREFIX}.error.invalid_time`, {
          row: rowNumber,
        }),
      )
    }

    // Validate location is not empty - multi-site specific
    if (!location.trim()) {
      throw new Error(
        t(`${MULTI_SITE_PREFIX}.error.invalid_location`, {
          row: rowNumber,
        }),
      )
    }

    return {
      siteId: siteId.trim(),
      resourceIds,
      startDate,
      startTime,
      endDate,
      endTime,
      location: location.trim(),
    }
  })
}

export function MultiSiteBulkImportRestrictionsPage() {
  const { t } = useTranslation()
  const [tsvContent, setTsvContent] = useState<string>('')
  const { pushAlert } = useAlertContext()

  const { bulkImportRestrictions, isPending } = useMultiSiteBulkImportRestrictionsMutation()

  const dialogProps = {
    title: t('multi_site_bulk_import.dialog.title'),
    description: t('multi_site_bulk_import.dialog.description'),
  }

  const onSubmit = async () => {
    try {
      const parsedRestrictions = parseTsvContent(tsvContent, t)

      await bulkImportRestrictions({
        restrictions: parsedRestrictions.map((restriction) => ({
          resourceOwnerId: restriction.siteId,
          resourceIds: restriction.resourceIds,
          startDate: restriction.startDate,
          startTime: restriction.startTime,
          endDate: restriction.endDate,
          endTime: restriction.endTime,
          location: restriction.location,
        })),
      })

      setTsvContent('')
      pushAlert({
        message: t('multi_site_bulk_import.success.text'),
        severity: 'success',
        title: t('multi_site_bulk_import.success.title'),
      })
    } catch (err) {
      if (err instanceof Error && !('response' in err)) {
        // It's a frontend validation error, use the message directly
        pushAlert({ message: err.message, severity: 'error' })
      } else {
        const error = errorHandler(err, t(`${COMMON_BULK_IMPORT_PREFIX}.error.unknown`))
        pushAlert({ message: error.message, severity: 'error' })
      }
    }
  }

  const placeholderText = `siteId\tAssets_ids\tstart_date\tstart_hour\tend_date\tend_hour\tlocation
SITE-UUID-1\tUUID1, UUID2\t2025-09-30\t04:00\t2025-10-01\t23:59\tnl
SITE-UUID-2\tUUID3, UUID4\t2025-09-29\t05:00\t2025-10-02\t23:59\tfi`

  return (
    <MainContentContainer>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography color="text.secondary" variant="body2">
            {t('multi_site_bulk_import.description')}
          </Typography>

          <TextAreaField
            label={t(`${COMMON_BULK_IMPORT_PREFIX}.tsv_label`)}
            placeholder={placeholderText}
            resize="vertical"
            rows={10}
            value={tsvContent}
            onChange={(e) => setTsvContent(e.target.value)}
          />

          <ConfirmationButton
            buttonProps={{
              startIcon: <UploadFileOutlined />,
              disabled: isPending || !tsvContent.trim(),
            }}
            dialogProps={dialogProps}
            onSubmit={onSubmit}
          >
            {t(`${COMMON_BULK_IMPORT_PREFIX}.submit`)}
          </ConfirmationButton>
        </Stack>
      </Paper>
    </MainContentContainer>
  )
}
